import React from "react";
import { Alert, Button, Modal } from "react-bootstrap";
import { DeviceAssignmentRow } from "@/features/devices/deviceAssignmentRows";
import { ineligibleFirstTrustReason } from "@/features/devices/firstTrust";
import styles from "./firstTrustModals.module.scss";

export type BulkFirstTrustResult = {
  succeeded: { serialNumber: string; expiresAt: string; firstTrustWindowId?: string }[];
  failed: { serialNumber: string; message: string }[];
};

type Props = {
  show: boolean;
  selected: DeviceAssignmentRow[];
  eligible: DeviceAssignmentRow[];
  ineligible: DeviceAssignmentRow[];
  authorizing: boolean;
  result: BulkFirstTrustResult | null;
  onClose: () => void;
  onConfirm: () => void;
};

const BulkFirstTrustModal: React.FC<Props> = ({
  show,
  selected,
  eligible,
  ineligible,
  authorizing,
  result,
  onClose,
  onConfirm,
}) => {
  const n = eligible.length;
  const canAuthorize = n > 0 && !result;

  return (
    <Modal
      show={show}
      onHide={onClose}
      centered
      dialogClassName={styles.firstTrustDialog}
      data-testid="bulk-first-trust-modal"
    >
      <Modal.Header closeButton>
        <Modal.Title>Authorize First Trust</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {!result && (
          <>
            <p>
              You are authorizing {n} KrossKast Player{n === 1 ? "" : "s"} to
              perform their first secure enrollment.
            </p>
            <p>
              Each Player receives its own independent First Trust
              authorization (10-minute window per serial). This is not a shared
              batch window.
            </p>
            <Alert variant="info" data-testid="bulk-first-trust-counts">
              {eligible.length} Players eligible
              {ineligible.length > 0 ? ` · ${ineligible.length} Players cannot be authorized` : ""}
            </Alert>
            {eligible.length > 0 && (
              <div className="mb-3">
                <strong>Eligible serials</strong>
                <ul data-testid="bulk-first-trust-eligible-list">
                  {eligible.map((d) => (
                    <li key={d.id}>{d.serialNumber}</li>
                  ))}
                </ul>
              </div>
            )}
            {ineligible.length > 0 && (
              <Alert variant="warning" data-testid="bulk-first-trust-ineligible-list">
                <strong>Cannot authorize</strong>
                <ul className="mb-0">
                  {ineligible.map((d) => (
                    <li key={d.id}>{ineligibleFirstTrustReason(d)}</li>
                  ))}
                </ul>
              </Alert>
            )}
            {selected.length === 0 && (
              <Alert variant="secondary">No Players selected.</Alert>
            )}
          </>
        )}

        {result && (
          <>
            {result.succeeded.length > 0 && (
              <Alert variant="success" data-testid="bulk-first-trust-success">
                Authorized {result.succeeded.length} Player
                {result.succeeded.length === 1 ? "" : "s"}. Successful windows
                were kept; they are not rolled back if others fail.
                <ul className="mb-0 mt-2">
                  {result.succeeded.map((s) => (
                    <li key={s.serialNumber}>
                      {s.serialNumber}
                      {s.expiresAt ? ` — expires ${s.expiresAt}` : ""}
                    </li>
                  ))}
                </ul>
              </Alert>
            )}
            {result.failed.length > 0 && (
              <Alert variant="danger" data-testid="bulk-first-trust-failed">
                {result.failed.length} Player
                {result.failed.length === 1 ? "" : "s"} failed. Retry those
                serials individually.
                <ul className="mb-0 mt-2">
                  {result.failed.map((f) => (
                    <li key={f.serialNumber}>
                      {f.serialNumber}: {f.message}
                    </li>
                  ))}
                </ul>
              </Alert>
            )}
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button
          variant="secondary"
          type="button"
          onClick={onClose}
          data-testid="bulk-first-trust-cancel"
        >
          {result ? "Close" : "Cancel"}
        </Button>
        {canAuthorize && (
          <Button
            variant="primary"
            type="button"
            disabled={authorizing}
            onClick={onConfirm}
            data-testid="bulk-first-trust-confirm"
          >
            {authorizing ? "Authorizing…" : "Authorize First Trust"}
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default BulkFirstTrustModal;
